import { addHours, isBefore, isValid, parse } from 'date-fns';
import { zonedTimeToUtc, formatInTimeZone } from "date-fns-tz";

export const isValidDate = (
  date: Date | number | string | undefined | null,
): date is Date | number | string =>
  date !== undefined && date !== null && isValid(new Date(date));

export const REMINDER_GRACE_PERIOD_HOURS = 2;

/**
 * Whether a manual reminder/invitation can still be sent for a consultation
 * scheduled at `date`: true while the consultation is in the future and for
 * REMINDER_GRACE_PERIOD_HOURS after the scheduled time. Compares absolute
 * instants (`consultation.date` is UTC, `new Date()` is now), so it is
 * timezone-agnostic.
 */
export const isWithinReminderWindow = (
  date: Date | number | string | undefined | null,
): boolean =>
  isValidDate(date) &&
  isBefore(new Date(), addHours(new Date(date), REMINDER_GRACE_PERIOD_HOURS));

export const parseAsUtc = (dateStr: string, formatStr: string) =>
  zonedTimeToUtc(parse(dateStr, formatStr, new Date()), "UTC");

export const formatAsUtc = (date: number|Date, formatStr: string) =>
  formatInTimeZone(date, "UTC", formatStr);

/**
 * Converts milliseconds to HH:mm format string (duration from midnight)
 * No timezone adjustments - purely arithmetic
 */
export const formatTimeOfDay = (milliseconds: number): string => {
  const totalMinutes = Math.floor(milliseconds / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`;
};

/**
 * Parses HH:mm format string to milliseconds (duration from midnight)
 * No timezone adjustments - purely arithmetic
 */
export const parseTimeOfDay = (timeString: string): number | null => {
  const match = timeString.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return (hours * 60 + minutes) * 60 * 1000;
};

export const isValidDuration = (value: unknown): value is number =>
  typeof value === 'number' && !isNaN(value) && value >= 0;
